/**
 * BoardDesignDemo — 5가지 게시판 디자인 패턴 비교 데모
 *
 * LayoutTestPage의 board 모듈 메인 영역에서 사용.
 * 사이드바에서 디자인 패턴을 선택하면 메인 영역이 해당 스타일로 렌더링됨.
 */
import React from 'react';
import {
  LayoutList, Bell, FileText, MessageSquare, Search, Plus, Eye,
  Heart, Pin, CheckCircle2, Circle, Clock, User, ArrowUp,
  ChevronDown, Hash, BookOpen, HelpCircle, ListTodo, Tag,
} from 'lucide-react';
import { cn } from '../../lib/utils';

// ─── 공통 Mock 데이터 ────────────────────────────────────────
const POSTS = [
  { id: 1, title: '2026년 2분기 OKR 공유', author: '김철수', avatar: 'K', date: '2시간 전', comments: 8, views: 42, likes: 5, pinned: true, snippet: '이번 분기 핵심 목표와 주요 결과를 공유합니다. 전사적으로 집중해야 할 3가지 방향...', tags: ['공지', 'OKR'], status: 'notice' },
  { id: 2, title: '신규 디자인 시스템 v2 가이드라인', author: '박지민', avatar: 'P', date: '5시간 전', comments: 12, views: 67, likes: 15, snippet: '디자인 토큰, 컴포넌트 규칙, 접근성 가이드를 포함한 새 디자인 시스템을 안내합니다...', tags: ['디자인', '가이드'], status: 'progress' },
  { id: 3, title: '4월 셋째주 주간 회의록', author: '이영희', avatar: 'L', date: '1일 전', comments: 3, views: 28, likes: 2, snippet: '참석자: 김철수, 이영희, 박지민. 주요 안건: API 리팩터링 일정 확정, QA 프로세스 개선...', tags: ['회의록'], status: 'done' },
  { id: 4, title: '서버 마이그레이션 일정 안내', author: '최동현', avatar: 'C', date: '2일 전', comments: 5, views: 91, likes: 8, snippet: '4/28(월) 22:00~익일 02:00 서버 점검이 예정되어 있습니다. 해당 시간 서비스 중단...', tags: ['공지', '인프라'], status: 'pending' },
  { id: 5, title: '프론트엔드 개발자 채용 공고', author: '김철수', avatar: 'K', date: '3일 전', comments: 15, views: 156, likes: 22, snippet: 'React/TypeScript 경력 3년 이상, 디자인 시스템 경험자 우대. 지원 마감: 5월 15일...', tags: ['채용'], status: 'open' },
  { id: 6, title: 'CI/CD 파이프라인 개선 방안 논의', author: '이영희', avatar: 'L', date: '4일 전', comments: 9, views: 73, likes: 11, snippet: 'GitHub Actions에서 빌드 시간이 평균 8분 → 3분으로 단축할 수 있는 방법을 제안합니다...', tags: ['개발', 'DevOps'], status: 'progress' },
];

// ─── 디자인 타입 정의 ────────────────────────────────────────
export type BoardDesignType = 'basecamp' | 'notion' | 'linear' | 'discourse' | 'dooray';

export const BOARD_DESIGNS: { type: BoardDesignType; name: string; icon: React.ElementType; desc: string }[] = [
  { type: 'basecamp', name: 'Basecamp 카드형', icon: LayoutList, desc: '카드 + 본문 미리보기' },
  { type: 'notion', name: 'Notion 테이블형', icon: BookOpen, desc: '다중 뷰 + 필터/정렬' },
  { type: 'linear', name: 'Linear 이슈형', icon: ListTodo, desc: '밀도 높은 상태 리스트' },
  { type: 'discourse', name: 'Discourse 포럼형', icon: MessageSquare, desc: '카테고리 탭 + 통계' },
  { type: 'dooray', name: 'Dooray 전통형', icon: FileText, desc: '번호 + 테이블 + 페이지네이션' },
];

// ─── A. Basecamp 카드형 ──────────────────────────────────────
const BasecampView: React.FC = () => (
  <div className="flex flex-col h-full">
    <header className="flex items-center gap-3 px-5 h-14 shrink-0 border-b border-border">
      <LayoutList className="size-5 text-primary" />
      <h2 className="text-base font-semibold">개발팀 게시판</h2>
      <span className="text-meta-sm px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">Basecamp</span>
      <div className="ml-auto">
        <button className="h-8 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center gap-1.5">
          <Plus className="size-3.5" />새 글
        </button>
      </div>
    </header>
    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
      {POSTS.map(p => (
        <div key={p.id} className="group rounded-xl border border-border bg-background hover:border-primary/30 hover:shadow-sm p-4 cursor-pointer transition-all duration-200">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold text-sm shrink-0 mt-0.5">{p.avatar}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {p.pinned && <Pin className="size-3.5 text-primary fill-primary" />}
                <h3 className="text-sm font-semibold text-[color:rgb(var(--text-title))] truncate">{p.title}</h3>
              </div>
              <p className="text-label text-muted-foreground line-clamp-2 leading-relaxed">{p.snippet}</p>
              <div className="flex items-center gap-3 mt-2.5 text-meta-sm text-muted-foreground">
                <span className="font-medium text-[color:rgb(var(--text-body))]">{p.author}</span>
                <span>·</span>
                <span>{p.date}</span>
                <span>·</span>
                <span className="flex items-center gap-0.5"><MessageSquare className="size-3" />{p.comments}</span>
                <span className="flex items-center gap-0.5"><Eye className="size-3" />{p.views}</span>
                <span className="flex items-center gap-0.5"><Heart className="size-3" />{p.likes}</span>
              </div>
            </div>
          </div>
          {p.tags.length > 0 && (
            <div className="flex gap-1.5 mt-3 ml-12">
              {p.tags.map(t => (
                <span key={t} className="text-meta-sm px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">#{t}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ─── B. Notion 테이블형 ──────────────────────────────────────
const NotionView: React.FC = () => {
  const [view, setView] = React.useState<'table' | 'board'>('table');
  return (
    <div className="flex flex-col h-full">
      <header className="flex items-center gap-3 px-5 h-14 shrink-0 border-b border-border">
        <BookOpen className="size-5 text-primary" />
        <h2 className="text-base font-semibold">개발팀 게시판</h2>
        <span className="text-meta-sm px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-600 font-medium">Notion</span>
        <div className="ml-4 flex gap-0.5 bg-muted rounded-lg p-0.5">
          {(['table', 'board'] as const).map(v => (
            <button key={v} onClick={() => setView(v)}
              className={cn('px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer',
                view === v ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}>
              {v === 'table' ? '테이블' : '보드'}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button className="h-7 px-2.5 rounded-md text-xs text-muted-foreground hover:bg-muted flex items-center gap-1 border border-border">
            <Tag className="size-3" />필터
          </button>
          <button className="h-7 px-2.5 rounded-md text-xs text-muted-foreground hover:bg-muted flex items-center gap-1 border border-border">
            <ChevronDown className="size-3" />정렬
          </button>
          <button className="h-8 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center gap-1.5">
            <Plus className="size-3.5" />새 글
          </button>
        </div>
      </header>

      {view === 'table' ? (
        <div className="flex-1 overflow-y-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border text-meta-sm font-semibold text-muted-foreground">
                <th className="text-left px-5 py-2.5 w-[40%]">제목</th>
                <th className="text-left px-3 py-2.5 w-[100px]">작성자</th>
                <th className="text-left px-3 py-2.5 w-[100px]">태그</th>
                <th className="text-left px-3 py-2.5 w-20">날짜</th>
                <th className="text-center px-3 py-2.5 w-[50px]">💬</th>
                <th className="text-center px-3 py-2.5 w-[50px]">👁</th>
              </tr>
            </thead>
            <tbody>
              {POSTS.map(p => (
                <tr key={p.id} className="border-b border-border/50 hover:bg-muted/50 cursor-pointer transition-colors">
                  <td className="px-5 py-2.5">
                    <div className="flex items-center gap-2">
                      {p.pinned && <span className="text-meta-sm bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium">고정</span>}
                      <span className="text-sm text-[color:rgb(var(--text-body))] truncate">{p.title}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5"><div className="flex items-center gap-1.5"><div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center text-meta-sm font-bold text-primary">{p.avatar}</div><span className="text-xs text-muted-foreground">{p.author}</span></div></td>
                  <td className="px-3 py-2.5"><div className="flex gap-1">{p.tags.slice(0, 2).map(t => <span key={t} className="text-meta-sm px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t}</span>)}</div></td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{p.date}</td>
                  <td className="px-3 py-2.5 text-center text-xs text-muted-foreground">{p.comments}</td>
                  <td className="px-3 py-2.5 text-center text-xs text-muted-foreground">{p.views}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-3 gap-3">
            {POSTS.map(p => (
              <div key={p.id} className="rounded-lg border border-border p-3 hover:shadow-md cursor-pointer transition-all bg-background">
                <h3 className="text-sm font-semibold text-[color:rgb(var(--text-title))] line-clamp-2 mb-2">{p.title}</h3>
                <p className="text-meta text-muted-foreground line-clamp-3 mb-3">{p.snippet}</p>
                <div className="flex items-center gap-2 text-meta-sm text-muted-foreground">
                  <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center text-meta-sm font-bold text-primary">{p.avatar}</div>
                  <span>{p.author}</span><span>·</span><span>{p.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── C. Linear 이슈형 ────────────────────────────────────────
const STATUS_MAP: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  notice: { icon: Bell, color: 'text-blue-500', label: '공지' },
  progress: { icon: Clock, color: 'text-amber-500', label: '진행중' },
  done: { icon: CheckCircle2, color: 'text-green-500', label: '완료' },
  pending: { icon: Circle, color: 'text-muted-foreground', label: '대기' },
  open: { icon: ArrowUp, color: 'text-violet-500', label: '오픈' },
};

const LinearView: React.FC = () => (
  <div className="flex flex-col h-full">
    <header className="flex items-center gap-3 px-5 h-14 shrink-0 border-b border-border">
      <ListTodo className="size-5 text-primary" />
      <h2 className="text-base font-semibold">개발팀 게시판</h2>
      <span className="text-meta-sm px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-600 font-medium">Linear</span>
      <div className="ml-auto flex gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <input className="h-8 pl-8 pr-3 text-xs rounded-lg border border-border bg-muted w-44 placeholder:text-muted-foreground" placeholder="검색..." readOnly />
        </div>
        <button className="h-8 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center gap-1.5">
          <Plus className="size-3.5" />새 글
        </button>
      </div>
    </header>
    <div className="flex-1 overflow-y-auto">
      {POSTS.map(p => {
        const st = STATUS_MAP[p.status] ?? STATUS_MAP.pending;
        const StIcon = st.icon;
        return (
          <div key={p.id} className="flex items-center gap-3 px-5 py-2.5 border-b border-border/40 hover:bg-muted/50 cursor-pointer transition-colors group">
            <StIcon className={cn('size-4 shrink-0', st.color)} />
            <span className="text-meta-sm font-medium text-muted-foreground w-12 shrink-0">{st.label}</span>
            <h3 className="text-sm text-[color:rgb(var(--text-body))] truncate flex-1 font-medium">{p.title}</h3>
            <div className="flex items-center gap-1.5 shrink-0">
              {p.tags.slice(0, 1).map(t => <span key={t} className="text-meta-sm px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t}</span>)}
            </div>
            <div className="flex items-center gap-1 shrink-0 text-meta-sm text-muted-foreground">
              <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center text-meta-sm font-bold text-primary">{p.avatar}</div>
            </div>
            <span className="text-meta-sm text-muted-foreground shrink-0 w-16 text-right">{p.date}</span>
            <div className="flex items-center gap-2 shrink-0 text-meta-sm text-muted-foreground">
              <span className="flex items-center gap-0.5"><MessageSquare className="size-3" />{p.comments}</span>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ─── D. Discourse 포럼형 ─────────────────────────────────────
const CATEGORIES = ['전체', '공지', 'QnA', '자유', '개발'];

const DiscourseView: React.FC = () => {
  const [cat, setCat] = React.useState('전체');
  return (
    <div className="flex flex-col h-full">
      <header className="flex items-center gap-3 px-5 h-14 shrink-0 border-b border-border">
        <MessageSquare className="size-5 text-primary" />
        <h2 className="text-base font-semibold">개발팀 게시판</h2>
        <span className="text-meta-sm px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-600 font-medium">Discourse</span>
        <div className="ml-auto">
          <button className="h-8 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center gap-1.5">
            <Plus className="size-3.5" />새 토픽
          </button>
        </div>
      </header>
      {/* 카테고리 탭 */}
      <div className="flex items-center gap-1 px-5 py-2 border-b border-border bg-muted/30">
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setCat(c)}
            className={cn('px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer',
              cat === c ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
            )}>
            {c}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {POSTS.map(p => (
          <div key={p.id} className="flex items-start gap-4 px-5 py-4 border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-colors">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold text-sm shrink-0">{p.avatar}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                {p.pinned && <span className="text-meta-sm bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-bold">📌 고정</span>}
                <h3 className="text-sm font-semibold text-[color:rgb(var(--text-title))] truncate">{p.title}</h3>
              </div>
              <div className="flex items-center gap-2 text-meta-sm text-muted-foreground mt-1">
                <span className="font-medium text-[color:rgb(var(--text-body))]">{p.author}</span>
                <span>·</span><span>{p.date}</span>
                <span>·</span><span>최종 활동 {p.date}</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0 text-meta-sm text-muted-foreground">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1"><MessageSquare className="size-3.5" /><strong>{p.comments}</strong></span>
                <span className="flex items-center gap-1"><Eye className="size-3.5" /><strong>{p.views}</strong></span>
                <span className="flex items-center gap-1"><Heart className="size-3.5" /><strong>{p.likes}</strong></span>
              </div>
              <div className="flex gap-1 mt-1">
                {p.tags.map(t => <span key={t} className="text-meta-sm px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-600">{t}</span>)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── E. Dooray 전통형 ────────────────────────────────────────
const DoorayView: React.FC = () => (
  <div className="flex flex-col h-full">
    <header className="flex items-center gap-3 px-5 h-14 shrink-0 border-b border-border">
      <FileText className="size-5 text-primary" />
      <h2 className="text-base font-semibold">개발팀 게시판</h2>
      <span className="text-meta-sm px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 font-medium">Dooray</span>
      <div className="ml-auto flex gap-2">
        <select className="h-8 px-2 text-xs rounded-lg border border-border bg-muted text-muted-foreground cursor-pointer">
          <option>전체 카테고리</option><option>공지</option><option>자유</option><option>QnA</option>
        </select>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <input className="h-8 pl-8 pr-3 text-xs rounded-lg border border-border bg-muted w-40 placeholder:text-muted-foreground" placeholder="검색..." readOnly />
        </div>
        <button className="h-8 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium">글쓰기</button>
      </div>
    </header>
    <div className="flex-1 overflow-y-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border text-meta-sm font-semibold text-muted-foreground uppercase bg-muted/50">
            <th className="text-center px-3 py-2.5 w-[50px]">No</th>
            <th className="text-left px-3 py-2.5 w-20">분류</th>
            <th className="text-left px-5 py-2.5">제목</th>
            <th className="text-left px-3 py-2.5 w-20">작성자</th>
            <th className="text-center px-3 py-2.5 w-[60px]">조회</th>
            <th className="text-center px-3 py-2.5 w-[60px]">추천</th>
            <th className="text-left px-3 py-2.5 w-20">작성일</th>
          </tr>
        </thead>
        <tbody>
          {POSTS.map((p, i) => (
            <tr key={p.id} className={cn('border-b border-border/50 hover:bg-muted/50 cursor-pointer transition-colors', p.pinned && 'bg-primary/[0.03]')}>
              <td className="px-3 py-3 text-center text-xs text-muted-foreground">{p.pinned ? <Pin className="size-3.5 text-primary mx-auto" /> : POSTS.length - i}</td>
              <td className="px-3 py-3"><span className="text-meta-sm px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">[{p.tags[0] || '자유'}]</span></td>
              <td className="px-5 py-3">
                <span className="text-sm text-[color:rgb(var(--text-body))]">{p.title}</span>
                {p.comments > 0 && <span className="text-meta-sm text-primary font-bold ml-1.5">[{p.comments}]</span>}
              </td>
              <td className="px-3 py-3 text-xs text-muted-foreground">{p.author}</td>
              <td className="px-3 py-3 text-center text-xs text-muted-foreground">{p.views}</td>
              <td className="px-3 py-3 text-center text-xs text-muted-foreground">{p.likes}</td>
              <td className="px-3 py-3 text-xs text-muted-foreground">{p.date}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {/* 페이지네이션 */}
    <div className="flex items-center justify-center gap-1 py-3 border-t border-border shrink-0">
      <button className="size-8 rounded-md text-xs text-muted-foreground hover:bg-muted">◀</button>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} className={cn('size-8 rounded-md text-xs font-medium transition-colors', n === 1 ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>{n}</button>
      ))}
      <button className="size-8 rounded-md text-xs text-muted-foreground hover:bg-muted">▶</button>
    </div>
  </div>
);

// ─── 메인 렌더러 ─────────────────────────────────────────────
const VIEWS: Record<BoardDesignType, React.FC> = {
  basecamp: BasecampView,
  notion: NotionView,
  linear: LinearView,
  discourse: DiscourseView,
  dooray: DoorayView,
};

export const BoardDesignMainContent: React.FC<{ design: BoardDesignType }> = ({ design }) => {
  const View = VIEWS[design];
  return <View />;
};
